// Anket oluşturma düzenleyicisinin SAF kuralları (spec 2026-10-16 S1–S4; plan netleşen 1–4).
import colors from '../../assets/styles/variables/colors';

// Sunucuda yeni soru tipi YOK: "Ölçek Sorusu" (SCALE) yalnız düzenleyicinin tipidir — sunucuya singleChoice + 1–5 puanlı beş
// seçenek olarak gider, yüklenirken (singleChoice + puanlar sırayla tam 1..5) yeniden tanınır. Puan sütunu hiçbir tipte
// gösterilmez; yeni tek seçim seçenekleri puansızdır (null), yüklenenlerin puanı gizli alanda korunur.

export const SCALE = 'scale';
export const SCALE_SIZE = 5;
export const MAX_HIGHLIGHTS = 3;

// Literal anahtarlar — check_translations ve anahtar testleri dinamik dizgiyi göremez.
export const SCALE_LABEL_KEYS = [
  'survey.scaleLabels.s1', 'survey.scaleLabels.s2', 'survey.scaleLabels.s3', 'survey.scaleLabels.s4', 'survey.scaleLabels.s5',
];

export const QUESTION_TYPES = [
  { id: SCALE, name: 'survey.scaleQuestion' },
  { id: 'singleChoice', name: 'survey.singleChoice' },
  { id: 'singleChoiceAlternate', name: 'survey.singleChoiceAlternate' },
  { id: 'multipleChoice', name: 'survey.multipleChoice' },
  { id: 'multipleChoiceAlternate', name: 'survey.multipleChoiceAlternate' },
  { id: 'ranking', name: 'survey.ranking' },
  { id: 'freeText', name: 'survey.freeText' },
];

export const QUESTION_TYPE_COLORS = {
  [SCALE]: colors.secondary.dark,
  singleChoice: '#2563eb',
  singleChoiceAlternate: '#3b82f6',
  multipleChoice: '#16a34a',
  multipleChoiceAlternate: '#ca8a04',
  ranking: '#dc2626',
  freeText: '#9333ea',
};

/** Seçenek listesi kullanıcıca düzenlenen tipler (ölçek HARİÇ — onun beş seçeneği sabittir). Kopyala/yapıştır bunlarda. */
export const OPTION_TYPES = ['singleChoice', 'singleChoiceAlternate', 'multipleChoice', 'multipleChoiceAlternate', 'ranking'];
/** Gizli puanı korunan tipler (çoklu/sıralama puan taşımaz). */
const SINGLE_TYPES = ['singleChoice', 'singleChoiceAlternate'];
export const TYPES_WITH_MAX_SELECTIONS = ['multipleChoice', 'multipleChoiceAlternate'];

export const typeLabelKey = (type) => (QUESTION_TYPES.find((x) => x.id === type) || { name: `survey.${type}` }).name;

let seq = 0;
/** Geçici kimlik — SAYI olmalı: questionEditPersistence.isServerId string'i sunucu kimliği sayar. */
export function tempId() {
  seq += 1;
  return Date.now() * 1000 + seq;
}

const toScore = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function isScaleScores(options) {
  return Array.isArray(options) && options.length === SCALE_SIZE
    && options.every((o, i) => toScore(o?.score) === i + 1);
}

export function scaleOptions(t) {
  return SCALE_LABEL_KEYS.map((key, i) => ({ id: tempId(), label: t(key), score: i + 1 }));
}

export function newQuestion(type, t) {
  let options = [];
  if (type === SCALE) options = scaleOptions(t);
  else if (OPTION_TYPES.includes(type)) options = [{ id: tempId(), label: '', score: null }];
  return {
    id: tempId(),
    text: '',
    type,
    isRequired: false,
    sectionTitle: '',
    maxSelections: null,
    isReverseScored: false,
    isHeadline: false,
    options,
  };
}

/** Sunucu sorusu (GetById) ya da AI üreticinin sorusu → düzenleyici sorusu (netleşen 1). */
export function fromServerQuestion(q) {
  const options = (q.options || []).map((o) => (typeof o === 'string'
    ? { id: tempId(), label: o, score: null }
    : { id: o.id || tempId(), label: o.label || '', score: toScore(o.score) }));
  const serverType = q.questionType || q.type || 'singleChoice';
  const type = serverType === 'singleChoice' && isScaleScores(options) ? SCALE : serverType;
  return {
    id: q.id || tempId(),
    text: q.questionText || q.text || '',
    type,
    isRequired: !!q.isRequired,
    sectionTitle: q.sectionTitle || '',
    maxSelections: q.maxSelections || null,
    isReverseScored: type === SCALE && !!q.isReverseScored,
    isHeadline: type === SCALE && !!q.isHeadline,
    options: type === 'freeText' ? [] : options,
  };
}

/** Düzenleyici sorusu → SurveyQuestionRequest gövdesi (create ve düzenleme aynı dönüşümü kullanır). */
export function toQuestionPayload(q) {
  const isScale = q.type === SCALE;
  let options = [];
  if (isScale) {
    options = q.options.map((o, i) => ({ label: o.label, score: i + 1 }));
  } else if (OPTION_TYPES.includes(q.type)) {
    options = q.options.map((o) => ({ label: o.label, score: SINGLE_TYPES.includes(q.type) ? toScore(o.score) : null }));
  }
  return {
    questionText: q.text,
    questionType: isScale ? 'singleChoice' : q.type,
    isRequired: !!q.isRequired,
    sectionTitle: q.sectionTitle || null,
    maxSelections: TYPES_WITH_MAX_SELECTIONS.includes(q.type) && q.maxSelections ? Number(q.maxSelections) : null,
    isReverseScored: isScale && !!q.isReverseScored,
    isHeadline: isScale && !!q.isHeadline,
    options,
  };
}

export function validateQuestions(title, questions) {
  if (!String(title || '').trim()) return false;
  if (!Array.isArray(questions) || questions.length === 0) return false;
  return questions.every((q) => {
    if (!String(q.text || '').trim()) return false;
    if (q.type === SCALE) return q.options.length === SCALE_SIZE && q.options.every((o) => o.label.trim());
    if (OPTION_TYPES.includes(q.type)) return q.options.length > 0 && q.options.every((o) => o.label.trim());
    return true;
  });
}

// Spec S3 — "Seçenekleri Kopyala" panosu: uygulama içi bellek, oturum boyu (sayfa yenilenene dek). Sistem panosu DEĞİL
// (puan da taşınır, tarayıcı izni gerekmez). Modül düzeyinde: başka bir ankete geçince de yapıştırılabilir.
let optionClipboard = null;

export function readOptionClipboard() {
  return optionClipboard;
}

export function writeOptionClipboard(question) {
  optionClipboard = (question?.options || []).map((o) => ({ label: o.label, score: toScore(o.score) }));
  return optionClipboard;
}

/** Yalnız testler için. */
export function clearOptionClipboard() {
  optionClipboard = null;
}

export function pasteOptions(question, clip) {
  if (!OPTION_TYPES.includes(question.type) || !Array.isArray(clip) || clip.length === 0) return question;
  return { ...question, options: clip.map((o) => ({ id: tempId(), label: o.label, score: toScore(o.score) })) };
}

export function canUsePreviousOptions(questions, index) {
  const q = questions[index];
  const prev = index > 0 ? questions[index - 1] : null;
  return !!q && !!prev && OPTION_TYPES.includes(q.type) && OPTION_TYPES.includes(prev.type)
    && q.options.every((o) => !o.label.trim()) && prev.options.some((o) => o.label.trim());
}

export function applyPreviousOptions(questions, index) {
  if (!canUsePreviousOptions(questions, index)) return questions;
  const source = questions[index - 1].options.filter((o) => o.label.trim());
  return questions.map((q, i) => (i === index ? pasteOptions(q, source) : q));
}

export const highlightCount = (questions) => questions.filter((q) => q.type === SCALE && q.isHeadline).length;

export function toggleHighlight(questions, id) {
  const target = questions.find((q) => q.id === id);
  if (!target || target.type !== SCALE) return questions;
  if (!target.isHeadline && highlightCount(questions) >= MAX_HIGHLIGHTS) return questions;
  return questions.map((q) => (q.id === id ? { ...q, isHeadline: !q.isHeadline } : q));
}
